import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { isNativeApp, isNativeIOS } from "../native/runtime";

const KEYBOARD_OPEN_THRESHOLD_PX = 120;
const KEYBOARD_ANIMATION_THRESHOLD_PX = 1;
const WEB_KEYBOARD_OPEN_SETTLE_MS = 360;
const WEB_KEYBOARD_QUIET_SETTLE_MS = 80;
const WEB_KEYBOARD_LARGE_CHANGE_PX = 96;
const CHAT_VIEWPORT_LOCK_CLASS = "vg-chat-viewport-lock";
export const NATIVE_CHAT_KEYBOARD_EVENT = "vg-native-chat-keyboard-viewport";
export const CHAT_KEYBOARD_TRANSITION =
  "250ms cubic-bezier(0.32, 0.72, 0, 1)";
export const CHAT_KEYBOARD_BOTTOM_GAP_PX = 10;

const CLOSED_VIEWPORT = Object.freeze({
  keyboardOpen: false,
  viewportPinned: false,
  overlayMode: false,
  top: 0,
  height: 0,
  bottom: 0,
  keyboardHeight: 0,
  layoutHeight: 0,
});

const nativeChatKeyboardSubscribers = new Set();
let nativeChatKeyboardSnapshot = CLOSED_VIEWPORT;
let nativeChatKeyboardController = null;
let nativeChatKeyboardReconcilePromise = Promise.resolve();

const readNativeChatLayoutHeight = () => {
  if (typeof window === "undefined") return 0;
  return Math.max(
    0,
    Number(window.innerHeight || 0),
    Number(window.visualViewport?.height || 0),
    Number(document.documentElement?.clientHeight || 0)
  );
};

export function buildNativeChatKeyboardViewport({
  layoutHeight = 0,
  keyboardHeight = 0,
  keyboardOpen = false,
} = {}) {
  if (!keyboardOpen) return CLOSED_VIEWPORT;

  const normalizedLayoutHeight = Math.max(0, Number(layoutHeight || 0));
  const normalizedKeyboardHeight = Math.min(
    normalizedLayoutHeight,
    Math.max(0, Number(keyboardHeight || 0))
  );
  const visibleBottom = Math.max(
    0,
    normalizedLayoutHeight - normalizedKeyboardHeight
  );

  return {
    keyboardOpen: true,
    viewportPinned: false,
    overlayMode: true,
    top: 0,
    height: visibleBottom,
    bottom: visibleBottom,
    keyboardHeight: normalizedKeyboardHeight,
    layoutHeight: normalizedLayoutHeight,
  };
}

const publishNativeChatKeyboardSnapshot = (nextSnapshot) => {
  nativeChatKeyboardSnapshot = nextSnapshot;
  nativeChatKeyboardSubscribers.forEach((listener) => {
    try {
      listener(nextSnapshot);
    } catch {}
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(NATIVE_CHAT_KEYBOARD_EVENT, {
        detail: nextSnapshot,
      })
    );
  }
};

const startNativeChatKeyboardController = async () => {
  const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
  const previousResizeMode = await Keyboard.getResizeMode()
    .then((result) => result?.mode || KeyboardResize.Native)
    .catch(() => KeyboardResize.Native);

  // Chat foreground layers own keyboard avoidance. Keeping WKWebView full-size
  // preserves the page behind the translucent keyboard and prevents the late
  // native frame crop that made the composer jump after the keyboard appeared.
  await Keyboard.setResizeMode({ mode: KeyboardResize.None });

  let restingLayoutHeight = readNativeChatLayoutHeight();
  const publishOpen = (info = {}) => {
    restingLayoutHeight = Math.max(
      restingLayoutHeight,
      readNativeChatLayoutHeight()
    );
    publishNativeChatKeyboardSnapshot(
      buildNativeChatKeyboardViewport({
        layoutHeight: restingLayoutHeight,
        keyboardHeight: info.keyboardHeight,
        keyboardOpen: true,
      })
    );
  };
  const publishClosed = () => {
    restingLayoutHeight = Math.max(
      restingLayoutHeight,
      readNativeChatLayoutHeight()
    );
    publishNativeChatKeyboardSnapshot(CLOSED_VIEWPORT);
  };
  const handles = [];
  try {
    for (const [eventName, listener] of [
      ["keyboardWillShow", publishOpen],
      ["keyboardDidShow", publishOpen],
      ["keyboardWillHide", publishClosed],
      ["keyboardDidHide", publishClosed],
    ]) {
      handles.push(await Keyboard.addListener(eventName, listener));
    }
  } catch (error) {
    await Promise.all(
      handles.map((handle) =>
        Promise.resolve(handle?.remove?.()).catch(() => {})
      )
    );
    await Keyboard.setResizeMode({ mode: previousResizeMode }).catch(() => {});
    throw error;
  }

  return {
    Keyboard,
    handles,
    previousResizeMode,
  };
};

const stopNativeChatKeyboardController = async (controller) => {
  publishNativeChatKeyboardSnapshot(CLOSED_VIEWPORT);
  await Promise.all(
    (controller?.handles || []).map((handle) =>
      Promise.resolve(handle?.remove?.()).catch(() => {})
    )
  );
  if (controller?.Keyboard?.setResizeMode) {
    await controller.Keyboard.setResizeMode({
      mode: controller.previousResizeMode,
    }).catch(() => {});
  }
};

const reconcileNativeChatKeyboardController = () => {
  nativeChatKeyboardReconcilePromise = nativeChatKeyboardReconcilePromise
    .then(async () => {
      if (nativeChatKeyboardSubscribers.size > 0 && !nativeChatKeyboardController) {
        nativeChatKeyboardController = await startNativeChatKeyboardController();
      }
      if (nativeChatKeyboardSubscribers.size === 0 && nativeChatKeyboardController) {
        const controller = nativeChatKeyboardController;
        nativeChatKeyboardController = null;
        await stopNativeChatKeyboardController(controller);
      }
    })
    .catch((error) => {
      console.warn("Native chat keyboard overlay is unavailable", error);
    });
};

const subscribeNativeChatKeyboard = (listener) => {
  nativeChatKeyboardSubscribers.add(listener);
  listener(nativeChatKeyboardSnapshot);
  reconcileNativeChatKeyboardController();

  return () => {
    nativeChatKeyboardSubscribers.delete(listener);
    reconcileNativeChatKeyboardController();
  };
};

const isEditableElement = (element) => {
  if (!element) return false;
  const tagName = String(element.tagName || "").toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    Boolean(element.isContentEditable)
  );
};

const isIOSWebBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const userAgent = String(navigator.userAgent || "");
  return (
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (/Macintosh|Mac OS X/i.test(userAgent) && Number(navigator.maxTouchPoints) > 1)
  );
};

const sameViewport = (left, right) => (
  left.keyboardOpen === right.keyboardOpen &&
  left.viewportPinned === right.viewportPinned &&
  left.overlayMode === right.overlayMode &&
  left.top === right.top &&
  left.height === right.height &&
  left.bottom === right.bottom &&
  left.keyboardHeight === right.keyboardHeight &&
  left.layoutHeight === right.layoutHeight
);

export function captureChatFeedViewportAnchor(feed) {
  if (!feed) return null;

  try {
    return {
      bottomDistance: Math.max(
        0,
        Number(feed.scrollHeight || 0) -
          Number(feed.scrollTop || 0) -
          Number(feed.clientHeight || 0)
      ),
    };
  } catch {
    return null;
  }
}

export function restoreChatFeedViewportAnchor(feed, anchor) {
  if (!feed || !anchor) return false;

  try {
    feed.scrollTop = Math.max(
      0,
      Number(feed.scrollHeight || 0) -
        Number(feed.clientHeight || 0) -
        Number(anchor.bottomDistance || 0)
    );
    return true;
  } catch {
    return false;
  }
}

export function useChatVisualViewport(
  isDisabled = false,
  {
    onBeforeViewportChange = null,
    onKeyboardFocus = null,
    trackOpeningAnimation = false,
    trackOpeningOffset = false,
  } = {}
) {
  const baselineHeightRef = useRef(0);
  const viewportRef = useRef(CLOSED_VIEWPORT);
  const keyboardIntentRef = useRef(false);
  const webOpeningStartedAtRef = useRef(0);
  const onBeforeViewportChangeRef = useRef(onBeforeViewportChange);
  const onKeyboardFocusRef = useRef(onKeyboardFocus);
  const [viewport, setViewport] = useState(CLOSED_VIEWPORT);
  onBeforeViewportChangeRef.current = onBeforeViewportChange;
  onKeyboardFocusRef.current = onKeyboardFocus;

  useLayoutEffect(() => {
    if (
      isDisabled ||
      trackOpeningAnimation ||
      !trackOpeningOffset ||
      !isIOSWebBrowser() ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const documentRoots = [document.documentElement, document.body].filter(
      Boolean
    );
    documentRoots.forEach((root) => {
      root.classList.add(CHAT_VIEWPORT_LOCK_CLASS);
    });
    return () => {
      documentRoots.forEach((root) => {
        root.classList.remove(CHAT_VIEWPORT_LOCK_CLASS);
      });
    };
  }, [isDisabled, trackOpeningAnimation, trackOpeningOffset]);

  useEffect(() => {
    if (isDisabled || typeof window === "undefined") {
      baselineHeightRef.current = 0;
      keyboardIntentRef.current = false;
      webOpeningStartedAtRef.current = 0;
      viewportRef.current = CLOSED_VIEWPORT;
      setViewport((current) => (
        current.keyboardOpen || current.viewportPinned || current.height > 0
          ? CLOSED_VIEWPORT
          : current
      ));
      return undefined;
    }

    let rafId = null;
    let settleTimerId = null;
    const commitViewport = (nextViewport) => {
      if (sameViewport(viewportRef.current, nextViewport)) return;
      onBeforeViewportChangeRef.current?.(viewportRef.current, nextViewport);
      viewportRef.current = nextViewport;
      setViewport(nextViewport);
    };

    if (isNativeIOS()) {
      return subscribeNativeChatKeyboard(commitViewport);
    }

    const scheduleSettledRead = (delay) => {
      if (settleTimerId != null) window.clearTimeout(settleTimerId);
      settleTimerId = window.setTimeout(() => {
        settleTimerId = null;
        readViewport({ allowWebCommit: true });
      }, Math.max(0, delay));
    };
    const readViewport = ({ allowWebCommit = false } = {}) => {
      const viewport = window.visualViewport;
      const layoutHeight = Math.max(0, Number(window.innerHeight || 0));
      const visualHeight = Math.max(0, Number(viewport?.height || layoutHeight));
      const visualTop = Math.max(0, Number(viewport?.offsetTop || 0));
      baselineHeightRef.current = Math.max(
        baselineHeightRef.current,
        layoutHeight,
        visualHeight
      );
      const heightReduction = Math.max(
        0,
        baselineHeightRef.current - visualHeight
      );
      const editableFocused = isEditableElement(document.activeElement);
      if (editableFocused) keyboardIntentRef.current = true;
      const keyboardWasOpen = viewportRef.current.keyboardOpen;
      // Safari also emits tiny viewport changes for browser chrome. Wait for a
      // real keyboard-sized reduction on web, but keep native animation tracking.
      const keyboardOpen =
        heightReduction > KEYBOARD_OPEN_THRESHOLD_PX ||
        (
          keyboardIntentRef.current &&
          (trackOpeningAnimation || keyboardWasOpen) &&
          heightReduction > KEYBOARD_ANIMATION_THRESHOLD_PX
        );
      if (!keyboardOpen && heightReduction <= KEYBOARD_ANIMATION_THRESHOLD_PX && !editableFocused) {
        keyboardIntentRef.current = false;
      }
      const nextViewport = keyboardOpen
        ? {
            keyboardOpen: true,
            viewportPinned: false,
            top: visualTop,
            height: visualHeight,
            bottom: visualTop + visualHeight,
          }
        : CLOSED_VIEWPORT;
      const pinWebChatShell = ({ preserveCommittedHeight = false } = {}) => {
        if (!trackOpeningOffset || trackOpeningAnimation) return;
        const committedViewport = viewportRef.current;
        const pinnedHeight = (
          preserveCommittedHeight && committedViewport.height > 0
        )
          ? committedViewport.height
          : baselineHeightRef.current;
        commitViewport({
          keyboardOpen: Boolean(
            preserveCommittedHeight && committedViewport.keyboardOpen
          ),
          viewportPinned: true,
          top: visualTop,
          height: pinnedHeight,
          bottom: visualTop + pinnedHeight,
        });
      };

      // Pin the chat shell before Safari begins panning the visual viewport.
      // Its top follows offsetTop immediately, while its height stays stable
      // until the keyboard animation has produced a trustworthy viewport size.
      const shouldPinWebChatShell = (
        trackOpeningOffset &&
        !trackOpeningAnimation &&
        !allowWebCommit &&
        (keyboardIntentRef.current || editableFocused || keyboardOpen)
      );
      if (shouldPinWebChatShell) {
        pinWebChatShell({
          preserveCommittedHeight: keyboardOpen && keyboardWasOpen,
        });
        if (!keyboardOpen) {
          webOpeningStartedAtRef.current = 0;
          if (settleTimerId != null) {
            window.clearTimeout(settleTimerId);
            settleTimerId = null;
          }
          return;
        }
      }

      if (!trackOpeningAnimation && keyboardOpen && !keyboardWasOpen) {
        const now = Date.now();
        if (!webOpeningStartedAtRef.current) {
          webOpeningStartedAtRef.current = now;
        }

        if (!allowWebCommit) {
          const openingElapsed = Math.max(
            0,
            now - webOpeningStartedAtRef.current
          );
          scheduleSettledRead(Math.max(
            WEB_KEYBOARD_QUIET_SETTLE_MS,
            WEB_KEYBOARD_OPEN_SETTLE_MS - openingElapsed
          ));
          return;
        }
      }

      if (
        !trackOpeningAnimation &&
        keyboardOpen &&
        keyboardWasOpen &&
        !allowWebCommit &&
        Math.abs(viewportRef.current.height - visualHeight) > WEB_KEYBOARD_LARGE_CHANGE_PX
      ) {
        scheduleSettledRead(WEB_KEYBOARD_QUIET_SETTLE_MS);
        return;
      }

      if (!keyboardOpen || allowWebCommit) {
        webOpeningStartedAtRef.current = 0;
      }
      if (settleTimerId != null) {
        window.clearTimeout(settleTimerId);
        settleTimerId = null;
      }

      commitViewport(nextViewport);
    };
    const pinWebChatShellBeforeFocus = () => {
      if (!trackOpeningOffset || trackOpeningAnimation) return;

      const visualViewport = window.visualViewport;
      const layoutHeight = Math.max(0, Number(window.innerHeight || 0));
      const visualHeight = Math.max(
        0,
        Number(visualViewport?.height || layoutHeight)
      );
      const visualTop = Math.max(
        0,
        Number(visualViewport?.offsetTop || 0)
      );
      baselineHeightRef.current = Math.max(
        baselineHeightRef.current,
        layoutHeight,
        visualHeight
      );

      const committedViewport = viewportRef.current;
      const preserveCommittedHeight = committedViewport.keyboardOpen;
      const pinnedHeight = (
        preserveCommittedHeight && committedViewport.height > 0
      )
        ? committedViewport.height
        : baselineHeightRef.current;
      const pinnedViewport = {
        keyboardOpen: Boolean(
          preserveCommittedHeight && committedViewport.keyboardOpen
        ),
        viewportPinned: true,
        top: visualTop,
        height: pinnedHeight,
        bottom: visualTop + pinnedHeight,
      };

      if (sameViewport(committedViewport, pinnedViewport)) return;
      // This must reach the DOM before Safari performs its focus auto-scroll.
      flushSync(() => commitViewport(pinnedViewport));
    };
    const schedule = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        readViewport();
      });
    };
    const reset = () => {
      baselineHeightRef.current = 0;
      webOpeningStartedAtRef.current = 0;
      if (settleTimerId != null) {
        window.clearTimeout(settleTimerId);
        settleTimerId = null;
      }
      schedule();
    };

    const handleFocusIn = (event) => {
      if (isEditableElement(event.target)) {
        keyboardIntentRef.current = true;
        onKeyboardFocusRef.current?.();
        pinWebChatShellBeforeFocus();
      }
      schedule();
    };
    const handlePreFocusPointer = (event) => {
      if (!isEditableElement(event.target)) return;
      keyboardIntentRef.current = true;
      onKeyboardFocusRef.current?.();
      pinWebChatShellBeforeFocus();
    };
    const handleFocusOut = () => schedule();

    readViewport();
    window.addEventListener("resize", schedule, { passive: true });
    window.addEventListener("orientationchange", reset, { passive: true });
    document.addEventListener("pointerdown", handlePreFocusPointer, true);
    document.addEventListener("touchstart", handlePreFocusPointer, true);
    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("focusout", handleFocusOut, true);
    window.visualViewport?.addEventListener?.("resize", schedule);
    window.visualViewport?.addEventListener?.("scroll", schedule);
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (settleTimerId != null) window.clearTimeout(settleTimerId);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", reset);
      document.removeEventListener("pointerdown", handlePreFocusPointer, true);
      document.removeEventListener("touchstart", handlePreFocusPointer, true);
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleFocusOut, true);
      window.visualViewport?.removeEventListener?.("resize", schedule);
      window.visualViewport?.removeEventListener?.("scroll", schedule);
    };
  }, [isDisabled, trackOpeningAnimation, trackOpeningOffset]);

  return viewport;
}

export function buildChatViewportStyle(
  viewport,
  { layoutViewportCoordinates = false } = {}
) {
  if (!viewport?.keyboardOpen && !viewport?.viewportPinned) return null;

  if (viewport?.overlayMode) {
    return {
      "--vg-chat-keyboard-inset": `${Math.max(
        0,
        Number(viewport.keyboardHeight || 0)
      )}px`,
      "--vg-chat-keyboard-resting-offset": `calc(max(env(safe-area-inset-bottom, 0px), ${CHAT_KEYBOARD_BOTTOM_GAP_PX}px) - ${CHAT_KEYBOARD_BOTTOM_GAP_PX}px)`,
    };
  }

  const viewportTop = Math.max(0, Number(viewport.top || 0));
  const viewportHeight = Math.max(0, Number(viewport.height || 0));
  const viewportBottom = Math.max(
    viewportTop,
    Number(viewport.bottom || viewportTop + viewportHeight)
  );

  // Mobile Safari can pan the visual viewport while focusing the composer, so
  // web fixed layers must include offsetTop. Native WebViews use layout coords.
  return {
    position: "fixed",
    insetInline: 0,
    top: layoutViewportCoordinates ? "0px" : `${viewportTop}px`,
    height: `${layoutViewportCoordinates ? viewportBottom : viewportHeight}px`,
    minHeight: 0,
  };
}

export function buildChatInputViewportStyle(
  viewport,
  bottomGap = CHAT_KEYBOARD_BOTTOM_GAP_PX,
  { layoutViewportCoordinates = false } = {}
) {
  if (!viewport?.keyboardOpen) return null;

  const normalizedGap = Math.max(0, Number(bottomGap || 0));
  if (viewport?.overlayMode) {
    const keyboardHeight = Math.max(0, Number(viewport.keyboardHeight || 0));
    return {
      bottom: "0px",
      top: "auto",
      transform: `translate3d(0, calc(0px - ${keyboardHeight}px - ${normalizedGap}px), 0)`,
      willChange: "transform",
    };
  }
  const viewportTop = Math.max(0, Number(viewport.top || 0));
  const viewportBottom = Math.max(
    viewportTop,
    Number(viewport.bottom || viewportTop + Number(viewport.height || 0))
  );
  const composerBottom = Math.max(viewportTop, viewportBottom - normalizedGap);

  if (!layoutViewportCoordinates) {
    return {
      bottom: "auto",
      top: `${composerBottom}px`,
      transform: "translate3d(0, -100%, 0)",
      willChange: "top, transform",
    };
  }

  return {
    bottom: "auto",
    top: "0px",
    transform: `translate3d(0, calc(${composerBottom}px - 100%), 0)`,
    willChange: "transform",
  };
}

export default function useDirectChatVisualViewport(
  isDesktop,
  feedRef = null,
  { layoutViewportCoordinates = isNativeApp() } = {}
) {
  const feedAnchorRef = useRef(null);
  const captureFeedAnchor = useCallback(() => {
    if (feedAnchorRef.current) return;
    feedAnchorRef.current = captureChatFeedViewportAnchor(feedRef?.current);
  }, [feedRef]);
  const viewport = useChatVisualViewport(isDesktop, {
    onBeforeViewportChange: captureFeedAnchor,
    onKeyboardFocus: captureFeedAnchor,
    trackOpeningAnimation: layoutViewportCoordinates,
    trackOpeningOffset: !layoutViewportCoordinates,
  });

  useLayoutEffect(() => {
    if (!feedAnchorRef.current) return;
    restoreChatFeedViewportAnchor(feedRef?.current, feedAnchorRef.current);
    feedAnchorRef.current = null;
  }, [feedRef, viewport.bottom, viewport.keyboardOpen]);

  return buildChatViewportStyle(viewport, { layoutViewportCoordinates });
}
